/**
 * カード一覧の並びを決める純粋関数。
 *
 * カードの並びはアウトラインの文書順とは独立で、各ノードの `card_priority`
 * （ノート全体で小さいほど前）で決まる。ここでは state の値だけから並びを計算し、
 * DOM には依存しない。
 */

export interface CardBoardNode {
  id: number;
  parent_id: number | null;
  card_priority?: number;
}

/**
 * ルート順・子順の深さ優先で、文書順の ID 配列を作る。
 * ノード一覧に無い ID は無視し、循環は seen で打ち切る。
 * ルートから辿れないノード（親が欠けたもの）は、末尾に元の順序で足す。
 */
export function flattenDocumentOrderIds(
  nodes: Iterable<CardBoardNode>,
  childrenOf: (parentId: number | null) => number[],
  rootIds: number[]
): number[] {
  const nodeList = [...nodes];
  const nodeIds = new Set<number>(nodeList.map((node) => node.id));
  const seen = new Set<number>();
  const result: number[] = [];

  const visit = (id: number): void => {
    if (seen.has(id) || !nodeIds.has(id)) {
      return;
    }
    seen.add(id);
    result.push(id);
    for (const childId of childrenOf(id)) {
      visit(childId);
    }
  };

  for (const rootId of rootIds) {
    visit(rootId);
  }
  for (const node of nodeList) {
    visit(node.id);
  }
  return result;
}

/**
 * カード並び順に ID を並べ替える。
 *
 * `card_priority` が有限数として設定されているノードはその昇順（同値は文書順）。
 * 未設定のノードは、文書順で前方に最も近い設定済みノードの直後へ補完する
 * （前方に無ければ、後方の最も近い設定済みノードの直前）。全件未設定なら文書順と一致する。
 * nodes に無い ID は除外する。
 */
export function sortCardIds(ids: number[], nodes: Map<number, CardBoardNode>, documentOrder: number[]): number[] {
  const documentIndex = new Map<number, number>();
  documentOrder.forEach((id, index) => {
    if (!documentIndex.has(id)) {
      documentIndex.set(id, index);
    }
  });

  const records = ids
    .map((id, inputIndex) => {
      const node = nodes.get(id);
      if (!node) {
        return null;
      }
      return {
        id,
        inputIndex,
        documentIndex: documentIndex.get(id) ?? documentOrder.length + inputIndex,
        priority:
          typeof node.card_priority === 'number' && Number.isFinite(node.card_priority)
            ? node.card_priority
            : undefined,
      };
    })
    .filter((record) => record !== null);

  const documentRecords = [...records].sort((a, b) => a.documentIndex - b.documentIndex || a.inputIndex - b.inputIndex);

  const previousPriority: Array<number | undefined> = [];
  let latestPriority: number | undefined;
  for (const record of documentRecords) {
    previousPriority.push(latestPriority);
    if (record.priority !== undefined) {
      latestPriority = record.priority;
    }
  }
  const nextPriority: Array<number | undefined> = [];
  let upcomingPriority: number | undefined;
  for (let index = documentRecords.length - 1; index >= 0; index -= 1) {
    nextPriority[index] = upcomingPriority;
    if (documentRecords[index].priority !== undefined) {
      upcomingPriority = documentRecords[index].priority;
    }
  }

  const effectivePriority = new Map<number, number>();
  documentRecords.forEach((record, index) => {
    if (record.priority !== undefined) {
      effectivePriority.set(record.id, record.priority);
    } else if (previousPriority[index] !== undefined) {
      effectivePriority.set(record.id, previousPriority[index]!);
    } else if (nextPriority[index] !== undefined) {
      // 後方の最も近い設定済みノードの「直前」に置く。連番（整数）とは衝突しないよう、
      // その値から 0.5 引いた中間の値にする（-1 だと手前の別の設定値と衝突しうる）。
      effectivePriority.set(record.id, nextPriority[index]! - 0.5);
    } else {
      effectivePriority.set(record.id, 0);
    }
  });

  return [...records]
    .sort((a, b) => {
      const diff = effectivePriority.get(a.id)! - effectivePriority.get(b.id)!;
      return diff !== 0 ? diff : a.documentIndex - b.documentIndex || a.inputIndex - b.inputIndex;
    })
    .map((record) => record.id);
}

/** 指定ノード自身を除く子孫を、文書順で返す。循環は打ち切る。 */
export function descendantIds(rootId: number, childrenOf: (parentId: number) => number[]): number[] {
  const seen = new Set<number>([rootId]);
  const result: number[] = [];
  const visit = (parentId: number): void => {
    for (const childId of childrenOf(parentId)) {
      if (seen.has(childId)) {
        continue;
      }
      seen.add(childId);
      result.push(childId);
      visit(childId);
    }
  };
  visit(rootId);
  return result;
}

/**
 * 表示がサブセット（デッキ等）のとき、サブセット内の差し込み位置を
 * 全体順（draggedId を除いた配列）への差し込み位置へ変換する。
 * 差し込み位置の直前にある表示中カードを錨にし、その直後へ入れる。
 * 直前にカードが無ければ、直後のカードの直前へ入れる。
 */
export function subsetInsertToGlobalIndex(
  fullOrder: number[],
  subsetIds: number[],
  draggedId: number,
  subsetInsertIndex: number
): number {
  const withoutDragged = subsetIds.filter((id) => id !== draggedId);
  const clampedIndex = Math.max(0, Math.min(subsetInsertIndex, withoutDragged.length));
  const fullWithoutDragged = fullOrder.filter((id) => id !== draggedId);
  const originalIndex = fullOrder.indexOf(draggedId);
  const fallback = originalIndex >= 0 ? Math.min(originalIndex, fullWithoutDragged.length) : 0;

  if (withoutDragged.length === 0) {
    return fallback;
  }
  const previousAnchor = clampedIndex > 0 ? withoutDragged[clampedIndex - 1] : undefined;
  if (previousAnchor !== undefined) {
    const anchorIndex = fullWithoutDragged.indexOf(previousAnchor);
    if (anchorIndex >= 0) {
      return anchorIndex + 1;
    }
  }
  const nextAnchor = clampedIndex < withoutDragged.length ? withoutDragged[clampedIndex] : undefined;
  if (nextAnchor !== undefined) {
    const anchorIndex = fullWithoutDragged.indexOf(nextAnchor);
    if (anchorIndex >= 0) {
      return anchorIndex;
    }
  }
  return fallback;
}

/** 配列から ID を抜き、クランプした位置へ差し込んだ新しい配列を返す（元は変更しない）。 */
export function moveIdToIndex(ids: number[], id: number, index: number): number[] {
  if (!ids.includes(id)) {
    return [...ids];
  }
  const result = ids.filter((value) => value !== id);
  const clampedIndex = Math.max(0, Math.min(index, result.length));
  result.splice(clampedIndex, 0, id);
  return result;
}
